import { Fieldset, HStack, Stack } from "@chakra-ui/react"
import { ActionButtonTypes, ButtonContent } from "@hydrologiq/shared/content/hooks/useButtonContent"
import { ReactNode, useRef } from "react"
import FormContainer from "./FormContainer"

export type FormActionOverrideProps = {
  onCancel: () => void
  onSubmit: () => Promise<boolean> | boolean
  onSecondaryAction?: () => boolean
  canSubmit: boolean
}
interface FormProps {
  onSubmit: (data: FormData) => Promise<boolean> | boolean
  onCancel?: () => void
  onBack?: () => void
  onSecondaryAction?: () => boolean
  heading?: string | ((props: FormActionOverrideProps) => ReactNode)
  helperText?: string | ReactNode
  children?: ReactNode
  loading?: boolean
  canSubmit?: boolean
  actionType: ActionButtonTypes
  actionContent?: ButtonContent
  actionVariant?: "primary" | "secondary"
  secondaryActionType?: ActionButtonTypes
  secondaryActionContent?: ButtonContent
  layout?: "row" | "column"
  ActionOverride?: (props: FormActionOverrideProps) => ReactNode
  outsideStack?: ReactNode
  useFormTag?: boolean // If false, uses div instead of form tag to avoid nested forms
}

const Form = (props: FormProps) => {
  const {
    onSubmit,
    onCancel,
    onBack,
    onSecondaryAction,
    heading,
    helperText,
    children,
    loading,
    actionType,
    actionContent,
    actionVariant,
    secondaryActionType,
    secondaryActionContent: defaultSecondaryActionContent,
    canSubmit = true,
    layout = "row",
    ActionOverride,
    outsideStack,
    useFormTag = true,
  } = props
  const formRef = useRef<HTMLFormElement | HTMLDivElement>(null)

  const submit = async () => {
    if (!formRef.current) return false
    // When using div instead of form, manually collect form data from inputs
    if (!useFormTag) {
      const formData = new FormData()
      const inputs = formRef.current.querySelectorAll("input, select, textarea")
      inputs.forEach((input: any) => {
        if (input.name) {
          formData.append(input.name, input.value)
        }
      })
      return await onSubmit(formData)
    }
    return await onSubmit(new FormData(formRef.current as HTMLFormElement))
  }

  const FormTag = useFormTag ? "form" : "div"

  return (
    <FormContainer
      onSubmit={submit}
      onCancel={onCancel}
      onBack={onBack}
      onSecondaryAction={onSecondaryAction}
      heading={heading}
      helperText={helperText}
      loading={loading}
      canSubmit={canSubmit}
      actionType={actionType}
      actionVariant={actionVariant}
      actionContent={actionContent}
      secondaryActionType={secondaryActionType}
      secondaryActionContent={defaultSecondaryActionContent}
      layout={layout}
      ActionOverride={ActionOverride}
      outsideStack={outsideStack}
      useFormTag={false}
    >
      <FormTag
        ref={formRef as any}
        onSubmit={
          useFormTag
            ? async (e: any) => {
                e.preventDefault()
                if (await onSubmit(new FormData(e.target as HTMLFormElement))) {
                  ;(formRef.current as HTMLFormElement)?.reset()
                }
              }
            : undefined
        }
        style={{ width: "100%" }}
      >
        <Fieldset.Root size="lg" maxW="md" opacity={loading ? 0.6 : 1} transition="opacity 0.2s">
          {(heading || helperText) && (
            <Stack>
              {heading && (
                <Fieldset.Legend>
                  <HStack>
                    {typeof heading === "function"
                      ? heading({ onCancel: onCancel || (() => {}), onSubmit: submit, onSecondaryAction, canSubmit })
                      : heading}
                  </HStack>
                </Fieldset.Legend>
              )}
              {helperText && <Fieldset.HelperText>{helperText}</Fieldset.HelperText>}
            </Stack>
          )}

          <Fieldset.Content gap={0}>
            <Stack gap={1} direction={layout == "row" ? "column" : "row"} height={"100%"} color="primary">
              {children}
            </Stack>
            {outsideStack}
          </Fieldset.Content>
        </Fieldset.Root>
      </FormTag>
    </FormContainer>
  )
}

export default Form
